import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft } from "lucide-react";

export default function VerifyOtpPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const email = sp.get("email") || "";
  const purpose = sp.get("purpose") || "login";
  const from = sp.get("from") || "/events";
  const devOtp = sp.get("dev_otp") || "";
  const { setSession } = useAuth();
  const [code, setCode] = useState(devOtp);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingReview, setPendingReview] = useState(null);
  const [countdown, setCountdown] = useState(10);
  const submitting = useRef(false);

  // Preview environments (SMTP blocked) surface the OTP as ?dev_otp=... so
  // the user can complete the flow even though the email never arrived. We
  // pre-fill the code but deliberately do NOT auto-submit — otherwise the
  // OTP screen flashes past and the user can't tell whether the flow is
  // actually working. Production doesn't set `MC_DEV_REVEAL_OTP` so this
  // branch is preview-only.
  // (Auto-submit removed 2026-08-29: previous behaviour surprised users
  // into thinking the OTP step had been bypassed.)

  useEffect(() => {
    if (!pendingReview) return;
    setCountdown(10);
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(t); nav("/login", { replace: true }); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [pendingReview, nav]);

  const submit = async (attempt) => {
    if (submitting.current) return;
    const c = (attempt || code).trim();
    setError(null);
    if (c.length !== 6) return setError("Enter the 6-digit code");
    submitting.current = true;
    setLoading(true);
    try {
      const r = await apiFetch("/auth/verify-otp", {
        method: "POST",
        body: JSON.stringify({ email, code: c, purpose }),
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = data?.detail || "Verification failed";
        if (typeof msg === "string" && msg.toLowerCase().includes("under review")) {
          setPendingReview(msg);
          return;
        }
        throw new Error(msg);
      }
      await setSession(data.token, data.user);
      nav(from, { replace: true });
    } catch (err) {
      setError(err.message || "Verification failed");
    } finally {
      setLoading(false);
      submitting.current = false;
    }
  };

  const onChange = (v) => {
    const digits = v.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (error) setError(null);
    if (digits.length === 6 && !pendingReview) setTimeout(() => submit(digits), 50);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-primary mb-4" data-testid="verify-back">
          <ChevronLeft size={16} /> Back
        </Link>
        <Card>
          <CardContent className="p-6 space-y-4">
            <div>
              <h1 className="text-2xl font-semibold">Enter verification code</h1>
              <p className="text-sm text-muted-foreground mt-2">
                We sent a 6-digit code to <span className="text-foreground font-medium">{email}</span>
              </p>
              {devOtp && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                  <span className="font-medium text-amber-900">Preview mode:</span>{" "}
                  <span className="text-amber-900">Your code has been emailed to you and is also shown here for convenience: <span className="font-mono font-semibold">{devOtp}</span> — auto-filled below.</span>
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="code">6-digit code</Label>
              <Input
                id="code"
                data-testid="verify-code"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                value={code}
                onChange={(e) => onChange(e.target.value)}
                placeholder="123456"
                className="text-center text-2xl tracking-[10px]"
              />
            </div>
            {pendingReview && (
              <div className="rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="pending-review-card">
                <div className="text-foreground">{pendingReview}</div>
                <div className="text-muted-foreground text-xs mt-2" data-testid="pending-review-countdown">
                  Returning to sign-in in {countdown} {countdown === 1 ? "second" : "seconds"}…
                </div>
              </div>
            )}
            {error && <div className="text-sm text-destructive" data-testid="verify-error">{error}</div>}
            <Button
              onClick={() => submit()}
              disabled={loading || !!pendingReview}
              data-testid="verify-submit"
              className="w-full"
            >
              {loading ? "Verifying…" : "Verify and continue"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
